const CUSTOM_ENVIRONMENTS_STORAGE_KEY = "cg_builder_envs_v1";
const ACTIVE_ENVIRONMENT_STORAGE_KEY = "cg_builder_active_env_v1";
const TOKEN_STORAGE_KEY_PREFIX = "cg_builder_token";
const API_PATH_SUFFIX = "/api/v1";
const DEFAULT_ENVIRONMENT_KEY =
  String(import.meta.env.VITE_DEFAULT_ENVIRONMENT_KEY || "prod").trim() || "prod";
const DEFAULT_ENVIRONMENT_LABEL =
  String(import.meta.env.VITE_DEFAULT_ENVIRONMENT_LABEL || "Production").trim() || "Production";
const ENV_SWITCHER_ORIGINS = (import.meta.env.VITE_ENV_SWITCHER_ORIGINS || "")
  .split(",")
  .map((value) => value.trim().replace(/\/+$/, ""))
  .filter(Boolean);

const DEFAULT_ENVIRONMENT_URL =
  import.meta.env.VITE_DEFAULT_API_BASE_URL ||
  import.meta.env.VITE_PROD_API_BASE_URL ||
  import.meta.env.VITE_API_BASE_URL ||
  "https://prodapi.genies.fun";

const BUILTIN_DEFAULT_ENVIRONMENT = {
  key: DEFAULT_ENVIRONMENT_KEY,
  label: DEFAULT_ENVIRONMENT_LABEL,
  baseUrl: sanitizeEnvironmentUrl(DEFAULT_ENVIRONMENT_URL),
  readonly: true
};

function sanitizeEnvironmentUrl(value) {
  return String(value || "").trim().replace(/\/+$/, "");
}

function toEnvironmentLabel(value) {
  return String(value || "").trim();
}

function parseStoredCustomEnvironments() {
  try {
    const raw = localStorage.getItem(CUSTOM_ENVIRONMENTS_STORAGE_KEY);
    if (!raw) return [];
    const payload = JSON.parse(raw);
    if (!Array.isArray(payload)) return [];
    return payload
      .map((item) => ({
        key: String(item?.key || "").trim(),
        label: toEnvironmentLabel(item?.label),
        baseUrl: sanitizeEnvironmentUrl(item?.baseUrl),
        readonly: false
      }))
      .filter((item) => item.key && item.label && item.baseUrl && item.key !== BUILTIN_DEFAULT_ENVIRONMENT.key);
  } catch {
    return [];
  }
}

function persistCustomEnvironments(customEnvironments) {
  localStorage.setItem(CUSTOM_ENVIRONMENTS_STORAGE_KEY, JSON.stringify(customEnvironments));
}

function makeEnvironmentKey(label) {
  const slug = toEnvironmentLabel(label)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  const randomPart = Math.random().toString(36).slice(2, 8);
  return `env-${slug || "custom"}-${randomPart}`;
}

export function getEnvironmentList() {
  return [BUILTIN_DEFAULT_ENVIRONMENT, ...parseStoredCustomEnvironments()];
}

export function getActiveEnvironmentKey() {
  const activeKey = String(localStorage.getItem(ACTIVE_ENVIRONMENT_STORAGE_KEY) || "").trim();
  const environments = getEnvironmentList();
  const exists = environments.some((item) => item.key === activeKey);
  if (exists) {
    return activeKey;
  }
  localStorage.setItem(ACTIVE_ENVIRONMENT_STORAGE_KEY, BUILTIN_DEFAULT_ENVIRONMENT.key);
  return BUILTIN_DEFAULT_ENVIRONMENT.key;
}

export function setActiveEnvironmentKey(nextKey) {
  const environments = getEnvironmentList();
  const resolvedKey = environments.some((item) => item.key === nextKey)
    ? nextKey
    : BUILTIN_DEFAULT_ENVIRONMENT.key;
  localStorage.setItem(ACTIVE_ENVIRONMENT_STORAGE_KEY, resolvedKey);
  return resolvedKey;
}

export function getActiveEnvironment() {
  const environments = getEnvironmentList();
  const activeKey = getActiveEnvironmentKey();
  return environments.find((item) => item.key === activeKey) || BUILTIN_DEFAULT_ENVIRONMENT;
}

export function buildApiBaseUrl(rawBaseUrl) {
  const normalized = sanitizeEnvironmentUrl(rawBaseUrl);
  if (!normalized) {
    return `${BUILTIN_DEFAULT_ENVIRONMENT.baseUrl}${API_PATH_SUFFIX}`;
  }
  if (normalized.toLowerCase().endsWith(API_PATH_SUFFIX)) {
    return normalized;
  }
  return `${normalized}${API_PATH_SUFFIX}`;
}

export function getActiveEnvironmentApiBaseUrl() {
  return buildApiBaseUrl(getActiveEnvironment().baseUrl);
}

export function getTokenStorageKey(environmentKey) {
  const safeKey =
    String(environmentKey || BUILTIN_DEFAULT_ENVIRONMENT.key).trim() || BUILTIN_DEFAULT_ENVIRONMENT.key;
  return `${TOKEN_STORAGE_KEY_PREFIX}_${safeKey}`;
}

export function validateEnvironmentUrl(rawBaseUrl) {
  const normalized = sanitizeEnvironmentUrl(rawBaseUrl);
  if (!normalized) {
    return "Environment URL is required.";
  }

  let parsedUrl;
  try {
    parsedUrl = new URL(normalized);
  } catch {
    return "Enter a valid URL.";
  }

  if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
    return "Use http:// or https://.";
  }

  const isLocalHost =
    parsedUrl.hostname === "localhost" ||
    parsedUrl.hostname === "127.0.0.1" ||
    parsedUrl.hostname === "[::1]";

  if (parsedUrl.protocol === "http:" && !isLocalHost) {
    return "Non-local environments must use https://.";
  }

  return "";
}

export function createCustomEnvironment({ label, baseUrl }) {
  const normalizedLabel = toEnvironmentLabel(label);
  const normalizedUrl = sanitizeEnvironmentUrl(baseUrl);
  const environments = parseStoredCustomEnvironments();
  const nextEnvironment = {
    key: makeEnvironmentKey(normalizedLabel),
    label: normalizedLabel,
    baseUrl: normalizedUrl,
    readonly: false
  };
  environments.push(nextEnvironment);
  persistCustomEnvironments(environments);
  return nextEnvironment;
}

export function updateCustomEnvironment(environmentKey, { label, baseUrl }) {
  const normalizedLabel = toEnvironmentLabel(label);
  const normalizedUrl = sanitizeEnvironmentUrl(baseUrl);
  const environments = parseStoredCustomEnvironments();
  const nextEnvironments = environments.map((item) =>
    item.key === environmentKey
      ? {
          ...item,
          label: normalizedLabel,
          baseUrl: normalizedUrl
        }
      : item
  );
  persistCustomEnvironments(nextEnvironments);
}

export function deleteCustomEnvironment(environmentKey) {
  const nextEnvironments = parseStoredCustomEnvironments().filter((item) => item.key !== environmentKey);
  persistCustomEnvironments(nextEnvironments);
  if (getActiveEnvironmentKey() === environmentKey) {
    setActiveEnvironmentKey(BUILTIN_DEFAULT_ENVIRONMENT.key);
  }
}

export function isProductionEnvironment(environment) {
  return String(environment?.label || "")
    .trim()
    .toLowerCase() === "production";
}

export function shouldShowEnvironmentSwitcher() {
  if (typeof window === "undefined") {
    return false;
  }

  if (ENV_SWITCHER_ORIGINS.length === 0) {
    return false;
  }

  const currentOrigin = String(window.location.origin || "").trim().replace(/\/+$/, "");
  if (!currentOrigin) {
    return false;
  }

  return ENV_SWITCHER_ORIGINS.includes(currentOrigin);
}
